"use client";

import Script from "next/script";
import { Suspense, useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import posthog from "posthog-js";
import { initGA, trackPageView } from "@/utils/analytics";
import { getRuntimeEnvironment } from "@/utils/helpers/runtimeEnvironment";
import { useCookieConsent } from "@/contexts/CookieConsentContext";

const runtimeEnvironment = getRuntimeEnvironment();
const isProduction = runtimeEnvironment.isProduction;

// ---------------------------------------------------------------------------
// Staging validation is complete: PostHog tracking is production-only again.
// (Previously BYPASS_POSTHOG_PROD_GUARD let staging traffic into the
// production project during integration validation.)
// ---------------------------------------------------------------------------
const BYPASS_POSTHOG_PROD_GUARD = false;
const shouldTrack = isProduction || BYPASS_POSTHOG_PROD_GUARD;

const GA_MEASUREMENT_ID = "G-7VZ0V2ETB7";
const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID;
const ADSENSE_PUBLISHER_ID = process.env.NEXT_PUBLIC_ADSENSE_PUBLISHER_ID;
const ADSENSE_ENABLED = process.env.NEXT_PUBLIC_ADSENSE_ENABLED === "true";
const CLARITY_PROJECT_ID = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID;

function RouteTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isFirstRender = useRef(true);
  const lastTrackedPathname = useRef<string | null>(null);
  // Measurement (GA + PostHog pageviews) requires the analytics flag.
  const { analyticsAllowed } = useCookieConsent();

  useEffect(() => {
    if (!isProduction) return;

    if (isFirstRender.current) {
      isFirstRender.current = false;
      lastTrackedPathname.current = pathname;
      return;
    }

    // Query-only changes (filters, sort, pagination) are not pageviews.
    if (pathname === lastTrackedPathname.current) return;
    lastTrackedPathname.current = pathname;

    if (!analyticsAllowed) return;

    const url =
      pathname +
      (searchParams?.toString() ? `?${searchParams.toString()}` : "");
    trackPageView({
      page_title: document.title,
      page_path: url,
      page_location: window.location.href,
    });
    posthog.capture("$pageview", {
      $current_url: window.location.href,
    });
  }, [pathname, searchParams, analyticsAllowed]);

  return null;
}

export function Analytics() {
  const { analyticsAllowed, marketingAllowed } = useCookieConsent();
  // Mixed third-party loaders (GTM/Clarity/AdSense) render when either flag
  // allows; measurement init below requires analytics specifically.
  const loadersAllowed = analyticsAllowed || marketingAllowed;

  useEffect(() => {
    if (!analyticsAllowed) return;
    if (isProduction) {
      console.log(
        `[Analytics] Runtime Environment: Production (${isProduction}). Initializing GA and tracking services...`,
      );
      if (typeof window !== "undefined") {
        initGA(GA_MEASUREMENT_ID);
      }
    } else {
      console.log(
        `[Analytics] Runtime Environment: Non-Production (${isProduction}). Tracking scripts disabled.`,
      );
    }
  }, [analyticsAllowed]);

  // Production-only: on other environments this component renders nothing,
  // so no measurement scripts load and RouteTracker never mounts.
  if (!isProduction) return null;
  return (
    <>
      {/* Mixed third-party loaders (GTM/Clarity/AdSense); measurement events
          inside them stay gated on the analytics flag above. */}
      {loadersAllowed && (
        <>
          {/* Google Analytics Script */}
          <Script
            strategy="afterInteractive"
            src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
          />

          <Script
            id="google-analytics-config"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${GA_MEASUREMENT_ID}', {
                  page_path: window.location.pathname,
                  debug_mode: false,
                  anonymize_ip: true,
                  cookie_flags: 'SameSite=None;Secure'
                });
              `,
            }}
          />

          {/* Google Tag Manager */}
          {GTM_ID && (
            <>
              <Script
                id="google-tag-manager"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{
                  __html: `
                    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                    })(window,document,'script','dataLayer','${GTM_ID}');
                  `,
                }}
              />
              <noscript>
                <iframe
                  src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
                  height="0"
                  width="0"
                  style={{ display: "none", visibility: "hidden" }}
                  title="Google Tag Manager"
                />
              </noscript>
            </>
          )}

          {/* Microsoft Clarity */}
          {CLARITY_PROJECT_ID && (
            <Script
              id="microsoft-clarity"
              strategy="afterInteractive"
              dangerouslySetInnerHTML={{
                __html: `
                  (function(c,l,a,r,i,t,y){
                    c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                    t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                    y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
                  })(window, document, "clarity", "script", "${CLARITY_PROJECT_ID}");
                `,
              }}
            />
          )}

          {/* Google AdSense */}
          {ADSENSE_ENABLED && ADSENSE_PUBLISHER_ID && (
            <Script
              id="google-adsense"
              src={`https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_PUBLISHER_ID}`}
              strategy="afterInteractive"
              crossOrigin="anonymous"
              onError={(e) => {
                console.error("AdSense script failed to load:", e);
              }}
            />
          )}
        </>
      )}

      {/* RouteTracker mounts only in production (see early return above). */}
      <Suspense fallback={null}>
        <RouteTracker />
      </Suspense>
    </>
  );
}
